import React, { useState, useContext, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Save, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { UserService } from '../../services/userService';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';  

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const { user, logout, updateAuthContext } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from || '/studies';
  
  // Check if this is a first-time password change
  const isFirstTimeChange = user?.requires_password_change;

  useEffect(() => {
    // Log the state for debugging
    console.log('[CHANGE_PASSWORD] Component mounted with state:', { 
      isFirstTimeChange,
      redirectPath,
      user: user ? { 
        email: user.email, 
        requiresPasswordChange: user.requires_password_change 
      } : null
    });
  }, [isFirstTimeChange, redirectPath, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'current_password') {
      setCurrentPassword(value);
    } else if (name === 'new_password') {
      setNewPassword(value);
    } else if (name === 'confirm_password') {
      setConfirmPassword(value);
    }
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    // Validation
    if (!isFirstTimeChange && !currentPassword) {
      setError('Current password is required');
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    
    // Prepare data for submission
    const passwordData = {
      new_password: newPassword,
      confirm_password: confirmPassword,
    };
    
    // Only include current password if this is not a first-time change
    if (!isFirstTimeChange) {
      passwordData.current_password = currentPassword;
    }

    console.log('[CHANGE_PASSWORD] Submitting password change with:', { 
      isFirstTimeChange,
      hasCurrentPassword: !!passwordData.current_password,
      email: user?.email
    });
    
    setIsLoading(true);
    
    try {
      const response = await UserService.changePassword(passwordData);
      setSuccessMessage('Password changed successfully');
      
      // Update user in context to clear the requires_password_change flag
      if (user && isFirstTimeChange) {
        const updatedUser = { ...user, requires_password_change: false };
        updateAuthContext({ user: updatedUser });
      }
      
      console.log('[CHANGE_PASSWORD] Password changed successfully, redirecting to:', redirectPath);
      
      // Redirect after successful password change
      setTimeout(() => {
        navigate(redirectPath);
      }, 1500);
    } catch (error) {
      console.error('[CHANGE_PASSWORD] Error:', error);
      
      if (typeof error === 'string') {
        // Direct error message from UserService
        setError(error);
      } else if (error.response && error.response.data) {
        // Get the complete error data directly from the API response
        const errorData = error.response.data;

        // Store the entire error response for display
        setError(errorData);
      } else {
        // Generic error fallback
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Fields to display based on whether this is a first-time password change
  const passwordFields = isFirstTimeChange 
    ? ['new', 'confirm'] // Skip current password for first-time changes
    : ['current', 'new', 'confirm'];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white shadow-lg rounded-lg overflow-hidden"
        >
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700 text-white">
            <h2 className="text-xl font-semibold flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              {isFirstTimeChange ? 'Set New Password' : 'Change Password'}
            </h2>
          </div>

          {isFirstTimeChange && (
            <div className="px-6 pt-4 text-gray-700">
              <p>You must set a new password before continuing.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-red-50 border-l-4 border-red-500 p-4 mb-4"
              >
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-1" />
                  <div className="text-red-700 w-full">
                    {typeof error === 'object' ? (
                      <div>
                        <p className="font-medium mb-2">{error.error || 'Password validation failed'}</p>
                        {error.validation_errors && error.validation_errors.length > 0 ? (
                          <ul className="list-disc pl-5 space-y-1">
                            {error.validation_errors.map((validationError, i) => (
                              <li key={i}>{validationError}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>{error.message || 'Please try again with a different password.'}</p>
                        )}
                      </div>
                    ) : (
                      // For string error messages, just show directly
                      <p>{error}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {successMessage && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-green-50 border-l-4 border-green-500 p-4 mb-4"
              >
                <p className="text-green-700">{successMessage}</p>
              </motion.div>
            )}

            {passwordFields.map((field) => (
              <div key={field} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.charAt(0).toUpperCase() + field.slice(1)} Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords[field] ? 'text' : 'password'}
                    name={`${field}_password`}
                    value={field === 'current' ? currentPassword : field === 'new' ? newPassword : confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility(field)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  >
                    {showPasswords[field] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            ))}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center items-center px-4 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              <Save className="w-5 h-5 mr-2" />
              {isLoading ? 'Changing Password...' : 'Save Password'}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ChangePassword;